import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Calculator,
  Flame,
  Beef,
  Wheat,
  Droplet,
  Activity,
  Target,
  Info
} from 'lucide-react';

// Use relative path for API calls to avoid CORS issues
const API = '/api';

const CalorieCheck = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    weight: '',
    height: '',
    age: '',
    gender: 'female',
    activity_level: 'moderate',
    goal: 'maintain'
  });

  const activityLevels = [
    { value: 'sedentary', label: 'Hareketsiz (Masa başı iş)' },
    { value: 'light', label: 'Hafif Aktif (Haftada 1-3 gün egzersiz)' },
    { value: 'moderate', label: 'Orta Aktif (Haftada 3-5 gün egzersiz)' },
    { value: 'active', label: 'Aktif (Haftada 6-7 gün egzersiz)' },
    { value: 'very_active', label: 'Çok Aktif (Günde 2 kez antrenman)' }
  ];

  const goals = [
    { value: 'lose', label: 'Kilo Vermek' },
    { value: 'maintain', label: 'Kilomu Korumak' },
    { value: 'gain', label: 'Kilo Almak (Kas)' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/ai/calories`,
        {
          weight: parseFloat(formData.weight),
          height: parseFloat(formData.height),
          age: parseInt(formData.age),
          gender: formData.gender,
          activity_level: formData.activity_level,
          goal: formData.goal
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResult(response.data);
      toast.success('Kalori hesaplaması tamamlandı!');
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || 'Günlük limitinize ulaştınız');
      } else if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Hesaplama yapılırken hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-blue-50 via-white to-blue-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => navigate('/ai-dietitian')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          AI Diyetisyen'e Dön
        </Button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Calculator className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Kalori ve Makro Hesaplama</h1>
          <p className="text-gray-600">Günlük kalori ve makro besin ihtiyacınızı öğrenin</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Form */}
          <Card>
            <CardHeader>
              <CardTitle>Bilgileriniz</CardTitle>
              <CardDescription>Kişisel bilgilerinizi girin</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="weight">Kilo (kg)</Label>
                    <Input
                      id="weight"
                      type="number"
                      placeholder="70"
                      value={formData.weight}
                      onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="height">Boy (cm)</Label>
                    <Input
                      id="height"
                      type="number"
                      placeholder="170"
                      value={formData.height}
                      onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="age">Yaş</Label>
                    <Input
                      id="age"
                      type="number"
                      placeholder="30"
                      value={formData.age}
                      onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="gender">Cinsiyet</Label>
                    <Select
                      value={formData.gender}
                      onValueChange={(value) => setFormData({ ...formData, gender: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="female">Kadın</SelectItem>
                        <SelectItem value="male">Erkek</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="activity">Aktivite Seviyesi</Label>
                  <Select
                    value={formData.activity_level}
                    onValueChange={(value) => setFormData({ ...formData, activity_level: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {activityLevels.map((level) => (
                        <SelectItem key={level.value} value={level.value}>
                          {level.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="goal">Hedefiniz</Label>
                  <Select
                    value={formData.goal}
                    onValueChange={(value) => setFormData({ ...formData, goal: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {goals.map((goal) => (
                        <SelectItem key={goal.value} value={goal.value}>
                          {goal.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                >
                  {loading ? 'Hesaplanıyor...' : 'Hesapla'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Results */}
          <div>
            {result ? (
              <div className="space-y-4">
                {/* Calorie Card */}
                <Card className="bg-gradient-to-br from-orange-50 to-red-50 border-orange-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <Flame className="h-8 w-8 text-orange-500 mr-3" />
                        <div>
                          <p className="text-sm text-orange-600">Günlük Kalori Hedefi</p>
                          <p className="text-4xl font-bold text-orange-700">{result.target_calories}</p>
                          <p className="text-sm text-orange-500">kcal</p>
                        </div>
                      </div>
                      <Badge className="bg-orange-100 text-orange-800">
                        {result.goal === 'lose' ? 'Kilo Verme' : result.goal === 'gain' ? 'Kilo Alma' : 'Koruma'}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-600 bg-white/50 p-3 rounded-lg">
                      <Info className="h-4 w-4 inline mr-1" />
                      BMR: {result.bmr} kcal | TDEE: {result.tdee} kcal
                    </div>
                  </CardContent>
                </Card>

                {/* Macros */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Günlük Makro Dağılımı</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <Beef className="h-6 w-6 text-red-500 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-red-600">{result.macros.protein}g</p>
                        <p className="text-sm text-red-500">Protein</p>
                      </div>
                      <div className="text-center p-4 bg-yellow-50 rounded-lg">
                        <Wheat className="h-6 w-6 text-yellow-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-yellow-600">{result.macros.carbs}g</p>
                        <p className="text-sm text-yellow-500">Karbonhidrat</p>
                      </div>
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <Droplet className="h-6 w-6 text-blue-500 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-blue-600">{result.macros.fat}g</p>
                        <p className="text-sm text-blue-500">Yağ</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Explanation */}
                <Card className="bg-gray-50">
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-600">{result.explanation}</p>
                  </CardContent>
                </Card>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => navigate('/ai-dietitian/weekly-plan')}
                  >
                    Beslenme Planı Oluştur
                  </Button>
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={() => navigate('/ai-dietitian/chat')}
                  >
                    Diyetisyene Sor
                  </Button>
                </div>
              </div>
            ) : (
              <Card className="h-full flex items-center justify-center min-h-[400px]">
                <CardContent className="text-center">
                  <Activity className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Bilgilerinizi girerek kalori hesaplaması yapın</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalorieCheck;
